import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import {
  hasConsented,
  getConsentPreferences,
  acceptAll,
  denyNonEssential,
  saveConsentPreferences,
} from './CookieConsentManager';

import css from './CookieConsentBanner.module.css';

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`${css.toggle} ${checked ? css.toggleOn : css.toggleOff} ${disabled ? css.toggleDisabled : ''}`}
  />
);

const CookieConsentBanner = () => {
  const location = useLocation();
  const isListingPage = /^\/l\//.test(location.pathname);

  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showFloating, setShowFloating] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    personalization: false,
    marketing: false,
  });

  useEffect(() => {
    if (hasConsented()) {
      setShowFloating(true);
      const saved = getConsentPreferences();
      if (saved) setPrefs(p => ({ ...p, ...saved }));
    } else {
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
    setShowSettings(false);
    setShowFloating(true);
  };

  const handleDenyNonEssential = () => {
    denyNonEssential();
    setVisible(false);
    setShowSettings(false);
    setShowFloating(true);
  };

  const handleSaveSettings = () => {
    saveConsentPreferences(prefs);
    setVisible(false);
    setShowSettings(false);
    setShowFloating(true);
  };

  const handleOpenSettings = () => {
    setVisible(true);
    setShowSettings(true);
    setShowFloating(false);
  };

  if (!visible && !showFloating) return null;

  return (
    <>
      {visible && (
        <div className={css.overlay}>
          <div className={css.banner}>
            {!showSettings ? (
              <>
                <h2 className={css.title}>Cookie Information</h2>
                <p className={css.text}>
                  On this site we use cookies and similar technologies to improve your experience,
                  analyze site traffic, and show personalized content. By clicking{' '}
                  <strong>Accept All</strong> you consent to all categories. You can also choose to{' '}
                  <strong>Reject Non-Essential</strong> cookies or <strong>Customize</strong> your
                  preferences at any time.
                </p>
                <div className={css.actions}>
                  <button className={css.btnPrimary} onClick={handleAcceptAll}>
                    Accept All
                  </button>
                  <button className={css.btnSecondary} onClick={handleDenyNonEssential}>
                    Reject Non-Essential
                  </button>
                  <button className={css.btnOutline} onClick={() => setShowSettings(true)}>
                    Customize
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={css.settingsHeader}>
                  <h2 className={css.title}>Customize Cookie Preferences</h2>
                  <button
                    className={css.closeBtn}
                    onClick={() => { setShowSettings(false); setVisible(false); setShowFloating(true); }}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
                <p className={css.settingsSubtitle}>
                  Choose which categories of cookies you allow. Necessary cookies are always active
                  and cannot be disabled.
                </p>

                <div className={css.settingsList}>
                  <div className={css.settingRow}>
                    <div className={css.settingInfo}>
                      <strong>Strictly Necessary</strong>
                      <p>Essential for the website to function. Cannot be disabled.</p>
                    </div>
                    <Toggle checked={true} onChange={() => {}} disabled={true} />
                  </div>

                  <div className={css.settingRow}>
                    <div className={css.settingInfo}>
                      <strong>Analytics (Anonymous usage)</strong>
                      <p>Help us understand how visitors interact with the site to improve it.</p>
                    </div>
                    <Toggle
                      checked={prefs.analytics}
                      onChange={v => setPrefs(p => ({ ...p, analytics: v }))}
                    />
                  </div>

                  <div className={css.settingRow}>
                    <div className={css.settingInfo}>
                      <strong>Personalization (Personal content storage)</strong>
                      <p>Allow the site to remember your preferences and show personalized content.</p>
                    </div>
                    <Toggle
                      checked={prefs.personalization}
                      onChange={v => setPrefs(p => ({ ...p, personalization: v }))}
                    />
                  </div>

                  <div className={css.settingRow}>
                    <div className={css.settingInfo}>
                      <strong>Marketing</strong>
                      <p>Used to track visitors across websites to display relevant advertising.</p>
                    </div>
                    <Toggle
                      checked={prefs.marketing}
                      onChange={v => setPrefs(p => ({ ...p, marketing: v }))}
                    />
                  </div>
                </div>

                <div className={css.actions}>
                  <button className={css.btnSecondary} onClick={handleDenyNonEssential}>
                    Reject All
                  </button>
                  <button className={css.btnPrimary} onClick={handleSaveSettings}>
                    Save Preferences
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showFloating && !visible && (
        <button
          className={isListingPage ? `${css.floatingBtn} ${css.floatingBtnHiddenOnMobile}` : css.floatingBtn}
          onClick={handleOpenSettings}
          aria-label="Cookie preferences"
          title="Cookie preferences"
        >
          🍪
        </button>
      )}
    </>
  );
};

export default CookieConsentBanner;
